import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Button, Container, Input, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { createRef, forwardRef, useImperativeHandle, useRef, useState } from "react";

/**
 * OptionButton is a customizable button component that supports toggling state.
 * It allows parent components to interact with its internal state using imperative methods.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.optionText - The text displayed on the button.
 * @param {boolean} props.check - Initial checked state of the button.
 * @param {string} props.platformOption - Additional metadata for the option (e.g., platform type).
 * @param {React.Ref} ref - A reference to expose methods to the parent component.
 *
 * @returns {JSX.Element} - The rendered OptionButton component.
 */
const OptionButton = forwardRef(function OptionButton({ optionText, check, platformOption }, ref) {
  const [checked, setChecked] = useState(check);

  const handleChange = () => {
    setChecked(!checked);
  };

  // Expose methods to the parent component via useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      getOptionState: () => ({ OptionText: optionText, State: checked, PlatformOption: platformOption }),
      setPlatformUnchecked: () => setChecked(false),
      setPlatformChecked: () => setChecked(true),
    }),
    [checked, optionText, platformOption] // Dependency array to ensure methods are updated when these values change
  );

  return (
    <Button
      variant="outlined"
      color={checked ? "primary" : "inherit"} // Set button color based on the checked state
      onClick={handleChange}
      fullWidth
      sx={{ minWidth: "200px" }}
    >
      {optionText}
    </Button>
  );
});

/**
 * ScraperOptionButtons is a component that renders buttons for different scraper options.
 * It uses a mapping to determine which options to display based on the `scraperClass`.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.scraperClass - The class of scraper (e.g., "ReviewPlatforms", "Forums").
 * @param {React.Ref} props.optionsRef - A reference to store and manage button refs.
 *
 * @returns {JSX.Element} - The rendered ScraperOptionButtons component.
 */
function ScraperOptionButtons({ scraperClass, optionsRef }) {
  const optionMap = {
    ReviewPlatforms: [
      "Ambitionbox",
      "Indeed",
      "Teamblind",
      "Infojobs",
      "Kununu",
      "Comparably",
      "Undelucram",
      "Ratemyplacement",
      "Ratemyapprenticeship",
      "Seek",
      "Zippia",
      "Payscale",
      "Fairygodboss",
      "Niche",
      "Lensa",
      "Dreamjob",
      "Breakroom",
      "Computrabajo",
    ],
    Forums: ["Layoff", "Gowork"],
    Glassdoor: ["English", "French", "German", "Dutch", "Portuguese", "Spanish", "Italian"],
    Fishbowl: ["company page", "phrases"],
    Reddit: ["phrase", "phrase_misc_subreddit", "employee_subreddit"],
    Twitter: ["Hashtags", "Phrases", "English", "French", "Japanese", "Hindi", "Arabic", "Spanish"],
  };

  const renderOptionButtons = (options) => {
    // Create an array of refs for each option
    optionsRef.current = options.map(() => createRef());
    // Map over the options to create button elements
    return options.map((option, index) => (
      <Grid key={index}>
        <OptionButton optionText={option} check={false} ref={optionsRef.current[index]} />
      </Grid>
    ));
  };

  return (
    <Grid
      container
      sx={{ py: 4, display: "grid", gridTemplateColumns: "repeat(5, auto)" }}
      spacing={1}
      justifyContent="center"
    >
      {renderOptionButtons(optionMap[scraperClass])}
    </Grid>
  );
}

/**
 * ActionButtons is a component that provides input for encoded arguments and various action buttons.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {function} props.decodeArgs - A function to decode the encoded arguments.
 * @param {function} props.getArgs - A function to get standard scraper arguments.
 * @param {function} props.selectAll - A function to select all options.
 * @param {function} props.clear - A function to clear selected options.
 *
 * @returns {JSX.Element} - The rendered ActionButtons component.
 */
const ActionButtons = ({ decodeArgs, getArgs, selectAll, clear }) => {
  const [encodedArg, setEncodedArg] = useState("");

  return (
    <Grid container justifyContent="center" spacing={2} sx={{ px: 5 }}>
      <Grid>
        <Input
          sx={{ mx: 0.5 }}
          id="arg-input"
          type="text"
          value={encodedArg}
          placeholder="Enter Encoded Arguments"
          onChange={(e) => setEncodedArg(e.target.value)}
        />
        <Button sx={{ mx: 2 }} variant="outlined" color="inherit" onClick={() => decodeArgs(encodedArg)}>
          Submit
        </Button>
      </Grid>
      <Grid>
        <Button variant="outlined" color="inherit" onClick={() => getArgs("standard")} sx={{ mx: 0.5 }}>
          Get Scraper Arguments!
        </Button>
        <Button variant="outlined" color="inherit" onClick={selectAll} sx={{ mx: 0.5 }}>
          Select All
        </Button>
        <Button variant="outlined" color="inherit" onClick={clear} sx={{ mx: 0.5 }}>
          Clear
        </Button>
      </Grid>
    </Grid>
  );
};

/**
 * DisplayData Component
 *
 * This component is responsible for displaying scraper arguments in a formatted manner.
 * It includes a button to copy the arguments to the clipboard.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string|Array} props.argData - The scraper arguments to be displayed.
 *                                        Can be a string or an array that will be converted to a string format.
 */
const DisplayData = ({ argData }) => {
  return (
    <Grid sx={{ px: 5 }}>
      <Box sx={{ display: "flex", p: "5px", pr: 8 }}>
        <Typography>Scraper Arguments:</Typography>
        <Button
          color="inherit"
          startIcon={<ContentCopyIcon />}
          onClick={() => {
            navigator.clipboard.writeText(`'[${argData}]'`);
          }}
        >
          Copy
        </Button>
      </Box>
      <Paper>
        <Box sx={{ minHeight: 150, borderRadius: 1, mb: 4 }}>
          <Typography sx={{ p: 3 }}>{Array.isArray(argData) ? JSON.stringify(argData) : argData}</Typography>
        </Box>
      </Paper>
    </Grid>
  );
};

/**
 * ScraperNames Component
 *
 * A component that displays a set of buttons for selecting different scrapers.
 * Each button changes the active scraper based on user interaction.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.curScraper - The currently selected scraper name.
 * @param {function} props.changeName - A function to update the current scraper name.
 */
const ScraperNames = ({ curScraper, changeName }) => {
  return (
    <Grid container sx={{ mb: 4 }} spacing={5} justifyContent="center">
      {["ReviewPlatforms", "Forums", "Fishbowl", "Glassdoor", "Reddit", "Twitter"].map((scraper) => (
        <Grid key={scraper}>
          <Button
            variant="outlined"
            color={curScraper === scraper ? "primary" : "inherit"}
            onClick={() => changeName(scraper)}
            sx={{ minWidth: "120px" }}
            size="large"
          >
            {scraper}
          </Button>
        </Grid>
      ))}
    </Grid>
  );
};

export function TaskArgGenerator() {
  const [scraperName, setName] = useState("ReviewPlatforms");
  const [argumentList, setArguments] = useState([]);
  const [encodedArgumentList, setEncodedArguments] = useState([]);
  const [displayData, setDisplayData] = useState("");
  const optionsRef = useRef([]);

  const clearSelection = () => {
    setArguments([]);
    setEncodedArguments([]);
    setDisplayData("");
  };

  const selectAll = () => {
    if (optionsRef.current === null) return;

    const allArgs = [];
    optionsRef.current.forEach((ref) => {
      ref.current.setPlatformChecked();
    });
    setArguments(allArgs);
  };

  const updateScraperName = (name) => {
    setName(name);
    setArguments([]);
    setDisplayData("");
  };

  const updateEncodedArguments = (arg) => {
    const curArguments = encodedArgumentList;
    if (encodedArgumentList.includes(arg)) {
      const index = encodedArgumentList.indexOf(arg);
      encodedArgumentList.splice(index, 1);
    } else {
      encodedArgumentList.push(arg);
      setEncodedArguments(curArguments);
    }
  };

  const getCompleteScraperArguments = (type) => {
    const accessToken = localStorage.getItem("access-token");
    let jsonData = {};

    if (scraperName === "") {
      alert("Please select a scraper");
      return;
    }

    if (type === "standard") {
      jsonData = {
        scraperName: scraperName,
        options: argumentList,
      };
    } else if (type === "encoded") {
      jsonData = {
        scraperName: scraperName,
        options: encodedArgumentList,
      };
    }
    const url = "/api/get-scraper-arguments";

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(jsonData),
    };

    fetch(url, options)
      .then((res) => res.json())
      .then((data) => {
        const parseData = JSON.parse(data);
        const formattedData = parseData.map((arg) => {
          return JSON.stringify(arg, null, 2);
        });
        setDisplayData(formattedData);
      });
  };

  const decodeArgs = (args) => {
    const accessToken = localStorage.getItem("access-token");
    const decodeArgsData = {
      scraperName: scraperName,
      encodedArguments: args,
    };

    const url = "api/decode-encoded-arguments";

    if (scraperName === "Fishbowl" || scraperName === "Reddit") {
      alert("Unable to return args for this scraper");
    } else if (args === "") {
      alert("Please enter your encoded args");
    } else if (scraperName === "") {
      alert("Please select a scraper");
    } else {
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(decodeArgsData),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data === null) {
            alert("Encoded argument did not find a match. Try changing the scraper class");
          } else {
            const curEncodedArguments = encodedArgumentList;
            curEncodedArguments.length = 0;
            setEncodedArguments(curEncodedArguments);
            data["arguments"].forEach((item) => updateEncodedArguments(item));
            getCompleteScraperArguments("encoded");
          }
        });
    }
  };

  return (
    <Box sx={{ pt: 4 }}>
      <Typography
        sx={{
          fontSize: "2rem",
          fontWeight: "bold",
          mb: 3,
          mx: 2,
        }}
        textAlign="center"
      >
        Task Argument Generator
      </Typography>
      <ScraperNames changeName={updateScraperName} curScraper={scraperName} />
      <Container>
        <Paper>
          <Box sx={{ minHeight: 250, borderRadius: 1, mb: 4 }}>
            <ScraperOptionButtons scraperClass={scraperName} optionsRef={optionsRef} />
          </Box>
        </Paper>
      </Container>
      <ActionButtons
        getArgs={getCompleteScraperArguments}
        decodeArgs={decodeArgs}
        clear={clearSelection}
        selectAll={selectAll}
      />
      <DisplayData argData={displayData} />
    </Box>
  );
}
